import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
  Accordion,
  Alert,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Container,
  TextField,
  Button,
  Stack,
  Typography,
  Paper,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { AddCircle, ExpandMore, RestartAlt } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { AuthContext } from '../contexts/AuthContext';
import { toast } from '../utils/alerts';

// APIs existentes en tu app
import { fetchVenta, createVenta, updateVenta } from '../api/ventas';
import { fetchCliente, searchClientesLight } from '../api/clientes';
import { searchTransportistasLight, fetchTransportista } from '../api/transportistas';
import { fetchProductoPorSku, searchProductosLight } from '../api/productos';

// Componente existente en tu app
import AsyncAutocomplete from '../components/AsyncAutocomplete';

const METODOS = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'contra_entrega', label: 'Contra entrega' },
];
const ESTADOS_ENVIO = [
  { value: 'pendiente_envio', label: 'Pendiente de envío' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'recibido', label: 'Recibido' },
];
const ESTADOS_PAGO = [
  { value: 'pendiente_pago', label: 'Pendiente de pago' },
  { value: 'pagada', label: 'Pagada' },
];
const fmtMoney = (n) => (Number.isFinite(+n) ? Number(n).toFixed(2) : '0.00');

const nuevaLinea = () => ({
  _producto: null,
  producto_id: '',
  cantidad: '1',
  precio_unitario: '',
  precio_lista: '',
  descuento: '0',
});

export default function VentaForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { start, stop } = useLoading();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.rol === 'admin' || user?.rol_id === 1;
  const [estadoVenta, setEstadoVenta] = useState('');
  const [motivoEdicion, setMotivoEdicion] = useState('');
  const [stockIssue, setStockIssue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingVenta, setLoadingVenta] = useState(isEdit);
  const [loadError, setLoadError] = useState('');
  const savingRef = useRef(false);
  const locked = estadoVenta === 'cancelada' || (estadoVenta === 'finalizada' && !isAdmin);

  const [cabecera, setCabecera] = useState({
    codigo: '',
    cliente_id: '',
    transportista_id: '',
    metodo_pago: 'transferencia',
    estado_envio: 'pendiente_envio',
    estado_pago: 'pendiente_pago',
  });

  // Dejamos una línea lista desde el inicio para que no haya que pulsar
  // "Agregar línea" antes de buscar o escanear el primer producto.
  const [lineas, setLineas] = useState(() => [nuevaLinea()]);
  const [clienteSel, setClienteSel] = useState(null);
  const [transportistaSel, setTransportistaSel] = useState(null);

  // 🔹 Preview de costo de envío del transportista seleccionado
  const [costoEnvio, setCostoEnvio] = useState(0);
    // =========================
  // ESCANEO GLOBAL (barcode) - VENTAS
  // =========================
  const scanBufferRef = useRef('');
  const scanLastTsRef = useRef(0);
  const scanBusyRef = useRef(false);

  const scanTimerRef = useRef(null);
  const scanTargetElRef = useRef(null);
  const scanTargetValueRef = useRef('');

  // Dedupe: evita procesar dos veces el mismo código muy seguido
  const lastProcessedRef = useRef({ code: '', ts: 0 });

  const clearScan = () => {
    scanBufferRef.current = '';
    scanLastTsRef.current = 0;
    scanTargetElRef.current = null;
    scanTargetValueRef.current = '';
    if (scanTimerRef.current) {
      clearTimeout(scanTimerRef.current);
      scanTimerRef.current = null;
    }
  };

  const addOrIncrementLineaByProducto = (prod) => {
    const pid = String(prod.id);

    setLineas((prev) => {
      const idx = prev.findIndex((ln) => String(ln.producto_id) === pid);

      if (idx >= 0) {
        const next = [...prev];
        const currentQty = Number(next[idx].cantidad) || 0;
        next[idx] = { ...next[idx], cantidad: String(currentQty + 1) };
        return next;
      }

      const nombre = String(prod.nombre ?? '').trim();
      const marca = String(prod.marca ?? '').trim();
      const presentacion = String(prod.presentacion ?? '').trim();
      const labelParts = [marca, presentacion].filter(Boolean).join(' — ');
      const label = labelParts ? `${nombre} — ${labelParts}` : nombre;

      const lineaProducto = {
        _producto: {
          id: pid,
          label,
          nombre,
          marca,
          presentacion,
          sku: prod.sku ?? '',
          precio_venta: prod.precio_venta,
        },
        producto_id: pid,
        cantidad: '1',
        precio_unitario: prod.precio_venta != null ? String(prod.precio_venta) : '',
        precio_lista: prod.precio_venta != null ? String(prod.precio_venta) : '',
        descuento: '0',
      };

      // Si la fila inicial está vacía, el escaneo la completa en lugar de
      // dejar una fila extra sin producto.
      const emptyIndex = prev.findIndex((ln) => !ln.producto_id);
      if (emptyIndex >= 0) {
        const next = [...prev];
        next[emptyIndex] = lineaProducto;
        return next;
      }

      return [...prev, lineaProducto];
    });
  };

  const processScanCode = async (codeRaw) => {
    const code = String(codeRaw || '').trim();
    if (!code) return;

    // dedupe rápido (scanner + timeout/enter/tab)
    const now = Date.now();
    const lastP = lastProcessedRef.current;
    if (lastP.code === code && now - lastP.ts < 900) return;
    lastProcessedRef.current = { code, ts: now };

    if (scanBusyRef.current) return;
    scanBusyRef.current = true;

    try {
      const found = await fetchProductoPorSku(code);

      if (!found) {
        toast.warn('No encontrado', `No se encontró el producto para: ${code}`);
        return;
      }

      addOrIncrementLineaByProducto(found);
    } catch (err) {
      console.error(err);
      toast.error('Error', err?.message || 'Error buscando producto por SKU.');
    } finally {
      scanBusyRef.current = false;
    }
  };

  const finalizeScan = (evt) => {
    const buf = String(scanBufferRef.current || '').trim();

    // restaurar input si se coló algo (primer dígito)
    const el = scanTargetElRef.current;
    const prevVal = scanTargetValueRef.current;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
      try {
        el.value = String(prevVal ?? '');
        el.dispatchEvent(new Event('input', { bubbles: true }));
      } catch {}
    }

    clearScan();

    if (buf.length >= 4) {
      if (evt) {
        evt.preventDefault();
        evt.stopPropagation();
      }
      processScanCode(buf);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // No interceptar campos numéricos de edición manual
      const active = document.activeElement;
      if (active && active.tagName === 'INPUT') {
        const n = active.getAttribute('name') || '';
        if (['cantidad', 'precio_unitario', 'descuento'].includes(n)) return;
      }

      const key = typeof e.key === 'string' ? e.key : '';
      const now = Date.now();

      // Enter / Tab finalizan (muchos scanners usan Tab)
      if (key === 'Enter' || key === 'NumpadEnter' || key === 'Tab') {
        finalizeScan(e);
        return;
      }

      // solo caracteres imprimibles
      if (key.length !== 1) return;

      const last = Number(scanLastTsRef.current || 0);
      const delta = last ? now - last : 9999;
      scanLastTsRef.current = now;

      // si pasa mucho tiempo, reinicia buffer
      if (delta > 300) {
        scanBufferRef.current = key;

        // guarda input enfocado para restaurar si se cuela texto del scanner
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          scanTargetElRef.current = active;
          scanTargetValueRef.current = active.value ?? '';
        } else {
          scanTargetElRef.current = null;
          scanTargetValueRef.current = '';
        }
      } else {
        scanBufferRef.current = String(scanBufferRef.current || '') + key;
      }

      // timeout por si NO manda Enter/Tab
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
      scanTimerRef.current = setTimeout(() => finalizeScan(null), 220);

      // si ya va largo, evita que siga escribiendo en inputs
      if (scanBufferRef.current.length >= 2 && delta < 120) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      clearScan();
    };
  }, []);


  useEffect(() => { stop(); }, [stop]);

  // Carga de venta para editar
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const r = await fetchVenta(id);
        const v = r?.data ?? r ?? {};
        setEstadoVenta(String(v.estado_venta || '').toLowerCase());
        setCabecera({
          codigo: v.codigo || '',
          cliente_id: v.cliente_id?.toString() || '',
          transportista_id: v.transportista_id?.toString() || '',
          metodo_pago: v.metodo_pago || 'transferencia',
          estado_envio: v.estado_envio || 'pendiente_envio',
          estado_pago: v.estado_pago || 'pendiente_pago',
        });

        if (v.cliente_id) {
          const rc = await fetchCliente(v.cliente_id);
          const cl = rc?.data ?? rc ?? null;
          if (cl) setClienteSel({ id: String(cl.id), label: cl.nombre });
        }
        if (v.transportista_id) {
          // traer transportista para preview de precio_envio
          try {
            const rt = await fetchTransportista(v.transportista_id);
            const tr = rt?.data ?? rt ?? null;
            if (tr) {
              setTransportistaSel({ id: String(tr.id), label: tr.nombre });
              setCostoEnvio(Number(tr.precio_envio || 0));
            }
          } catch {
            setCostoEnvio(0);
          }
        }

        const fetchedLineas = Array.isArray(v.lineas) ? v.lineas : [];
     setLineas(
  fetchedLineas.length ? fetchedLineas.map((ln) => ({
    _producto:
      ln.producto_id && ln.producto_nombre
        ? {
            id: String(ln.producto_id),
            label: ln.producto_nombre,
            nombre: ln.producto_nombre,        // 👈 añadimos nombre también
            precio_venta: ln.precio_unitario,
          }
        : null,
    producto_id: (ln.producto_id ?? '').toString(),
    cantidad: (ln.cantidad ?? '').toString(),
    precio_unitario: (ln.precio_unitario ?? '').toString(),
    // Para una venta ya guardada preservamos el precio que se aplicó en ese momento.
    precio_lista: (ln.precio_unitario ?? '').toString(),
    descuento: (ln.descuento || 0).toString(),
  })) : [nuevaLinea()]
);

      } catch {
        setLoadError('No se pudo cargar la venta. Vuelve al listado e inténtalo de nuevo.');
      } finally {
        setLoadingVenta(false);
      }
    })();
  }, [id]);

  // 🔹 Cuando cambia el transportista seleccionado en cabecera, traer su precio_envio para el preview
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!cabecera.transportista_id) {
        if (mounted) setCostoEnvio(0);
        return;
      }
      try {
        const respT = await fetchTransportista(Number(cabecera.transportista_id));
        const tr = respT?.data ?? respT ?? null;
        const precio = Number(tr?.precio_envio ?? 0);
        if (mounted) setCostoEnvio(Number.isFinite(precio) ? precio : 0);
      } catch {
        if (mounted) setCostoEnvio(0);
      }
    })();
    return () => { mounted = false; };
  }, [cabecera.transportista_id]);

  const handleCabeceraChange = (e) => {
    const { name, value } = e.target;
    setCabecera((c) => ({ ...c, [name]: value }));
  };

  const handleLineaChange = (idx, e) => {
    const { name, value } = e.target;
    setLineas((l) => l.map((ln, i) => (i === idx ? { ...ln, [name]: value } : ln)));
  };

  const restaurarPrecioLista = (idx) => {
    setLineas((l) => l.map((ln, i) => (
      i === idx ? { ...ln, precio_unitario: String(ln.precio_lista ?? '') } : ln
    )));
  };

  const addLinea = () =>
    setLineas((l) => [
      ...l,
      nuevaLinea(),
    ]);

  const removeLinea = (idx) =>
    setLineas((l) => {
      const next = l.filter((_, i) => i !== idx);
      return next.length ? next : [nuevaLinea()];
    });

  const toNum = (v) => Number(String(v).replace(',', '.')) || 0;

  const precioListaLinea = (ln) => toNum(ln.precio_lista ?? ln._producto?.precio_venta);
  const tienePrecioEspecial = (ln) => {
    const lista = precioListaLinea(ln);
    return Boolean(ln.producto_id) && lista > 0 && Math.abs(toNum(ln.precio_unitario) - lista) > 0.004;
  };

  const calcularSubtotal = (ln) => {
    const c = toNum(ln.cantidad);
    const pu = toNum(ln.precio_unitario);
    const d = toNum(ln.descuento);
    return c * pu * (1 - d / 100);
  };

  const totalVenta = useMemo(
    () => (lineas || []).reduce((sum, ln) => sum + calcularSubtotal(ln), 0),
    [lineas]
  );

  // 🔹 Preview total + envío (la comisión real la calcula el backend al guardar)
  const totalConEnvioPreview = useMemo(
    () => Number(totalVenta || 0) + Number(costoEnvio || 0),
    [totalVenta, costoEnvio]
  );

  const fetchClientesPage = async ({ q, page, limit }) => {
    const res = await searchClientesLight({ q, page, pageSize: limit });
    const items = res?.items || res?.data || [];
    return { items: items.map((c) => ({ id: String(c.id), label: c.nombre })), hasMore: !!res?.hasMore };
  };

  const fetchTransportistasPage = async ({ q, page, limit }) => {
    const res = await searchTransportistasLight({ q, page, pageSize: limit });
    const items = res?.items || res?.data || [];
    return { items: items.map((t) => ({ id: String(t.id), label: t.nombre })), hasMore: !!res?.hasMore };
  };

  // 🔹 Productos: ahora incluimos marca y presentación y preparamos datos para 2 líneas
  const fetchProductosPage = async ({ q, page, limit }) => {
    const res = await searchProductosLight({ q, page, pageSize: limit });
    const items = res?.items || res?.data || [];
    return {
      items: items.map((p) => ({
        id: String(p.id),
        nombre: p.nombre,
        marca: p.marca,
        presentacion: p.presentacion,
        precio_venta: p.precio_venta,
        label: p.nombre, // usado como fallback / etiqueta básica
      })),
      hasMore: !!res?.hasMore,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingRef.current || locked || loadingVenta || loadError) return;

    if (!cabecera.cliente_id) return toast.warn('Cliente requerido', 'Debe seleccionar un cliente.');
    const lineasParaGuardar = (lineas || []).filter((ln) => Boolean(ln.producto_id));
    if (!lineasParaGuardar.length) {
      return toast.warn('Sin productos', 'Busque o escanee al menos un producto para continuar.');
    }

    const hayLineaSinProducto = (lineas || []).some((ln) =>
      !ln.producto_id && (
        Boolean(String(ln.precio_unitario || '').trim()) ||
        toNum(ln.descuento) !== 0 ||
        (String(ln.cantidad || '') && String(ln.cantidad) !== '1')
      )
    );
    if (hayLineaSinProducto) {
      return toast.warn('Producto requerido', 'Complete o elimine las líneas que no tienen producto.');
    }

    for (const ln of lineasParaGuardar) {
      if (toNum(ln.cantidad) <= 0 || toNum(ln.precio_unitario) <= 0) {
        return toast.warn('Datos incompletos', 'Cada producto requiere cantidad y precio de venta mayores que cero.');
      }
      if (toNum(ln.descuento) < 0 || toNum(ln.descuento) > 100) {
        return toast.warn('Descuento inválido', 'El descuento debe estar entre 0% y 100%.');
      }
    }

    const payloadBase = {
      cliente_id: Number(cabecera.cliente_id),
      transportista_id: cabecera.transportista_id ? Number(cabecera.transportista_id) : null,
      metodo_pago: cabecera.metodo_pago,
      estado_envio: cabecera.estado_envio,
      estado_pago: cabecera.estado_pago,
      lineas: lineasParaGuardar.map((ln) => ({
        producto_id: Number(ln.producto_id),
        cantidad: toNum(ln.cantidad),
        // El backend lo guarda como precio_unitario histórico en el detalle,
        // sin cambiar el precio de lista del producto.
        precio_venta: toNum(ln.precio_unitario),
        descuento: toNum(ln.descuento),
      })),
    };
    const payload = isEdit ? { ...payloadBase, codigo: cabecera.codigo, motivo_edicion: motivoEdicion } : payloadBase;

    savingRef.current = true;
    setSaving(true);
    setStockIssue(null);
    try {
      if (isEdit) await updateVenta(id, payload);
      else await createVenta(payload);
      toast.ok('Guardado', 'La venta se guardó correctamente.', { timer: 1800 });
      start();
      nav('/ventas');
    } catch (err) {
      const raw = err?.response?.data?.error || err?.message || 'Error desconocido';
      if (err?.response?.data?.code === 'STOCK_INSUFICIENTE') {
        setStockIssue({ message: raw, id: err.response.data.verificacion_id });
      }
      toast.error('No se pudo guardar', raw);
      console.error('Error guardando venta', err?.response?.data || err);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1, md: 2.5 }, pb: { xs: 3, md: 4 } }}>
      <Paper sx={{ borderRadius: '18px', overflow: 'visible' }}>
        {/* CONTENIDO PRINCIPAL */}
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {isEdit ? 'Editar venta' : 'Nueva venta'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Selecciona los productos y guarda el precio aplicado para esta venta.
              </Typography>
            </Box>
            <Chip
              size="small"
              color={isEdit ? 'info' : 'warning'}
              variant="outlined"
              label={isEdit ? 'Edición en curso' : 'Borrador'}
            />
          </Stack>

          <form id="venta-form" onSubmit={handleSubmit}>
            {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
            {locked && <Alert severity="warning" sx={{ mb: 2 }}>
              {estadoVenta === 'cancelada' ? 'Una venta cancelada no se puede editar.' : 'Solo un administrador puede editar esta venta finalizada.'}
            </Alert>}
            {estadoVenta === 'finalizada' && isAdmin && <Stack spacing={1} sx={{ mb: 2 }}>
              <Alert severity="info">Estás corrigiendo una venta finalizada. Se recalcularán los totales y el stock, y se guardará un historial de la edición.</Alert>
              <TextField label="Motivo de la corrección (opcional)" value={motivoEdicion}
                inputProps={{ maxLength: 500 }} onChange={(event) => setMotivoEdicion(event.target.value)} />
            </Stack>}
            {stockIssue && <Alert severity="warning" sx={{ mb: 2 }}
              action={stockIssue.id ? <Button color="inherit" href="/verificaciones-stock" target="_blank" rel="noopener">Ver pendientes</Button> : null}>
              {stockIssue.message}
            </Alert>}
            <fieldset disabled={locked || loadingVenta || Boolean(loadError) || saving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
            {/* === CABECERA === */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start", mb: 2 }}>
              {isEdit && (
                <TextField
                  label="Código"
                  name="codigo"
                  value={cabecera.codigo}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ flex: "1 1 180px", minWidth: 180 }}
                />
              )}

              <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
                <AsyncAutocomplete
                  label="Cliente"
                  value={clienteSel}
                  onChange={(val) => {
                    setClienteSel(val);
                    setCabecera((c) => ({ ...c, cliente_id: val?.id ?? "" }));
                  }}
                  fetchPage={fetchClientesPage}
                  placeholder="Buscar cliente..."
                />
              </Box>

              <TextField
                select
                label="Método de pago"
                name="metodo_pago"
                value={cabecera.metodo_pago}
                onChange={handleCabeceraChange}
                fullWidth
                sx={{ flex: "1 1 200px", minWidth: 200 }}
              >
                {METODOS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Accordion
              disableGutters
              elevation={0}
              defaultExpanded={isEdit}
              sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box>
                  <Typography fontWeight={600}>Entrega y estados (opcional)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Usa los valores predeterminados si no necesitas modificar el envío o los estados.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
              <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
                <AsyncAutocomplete
                  label="Transportista"
                  value={transportistaSel}
                  onChange={(val) => {
                    setTransportistaSel(val);
                    setCabecera((c) => ({ ...c, transportista_id: val?.id ?? "" }));
                    // El useEffect traerá precio_envio para el preview
                  }}
                  fetchPage={fetchTransportistasPage}
                  placeholder="Buscar transportista..."
                />
                <Button
                  size="small"
                  onClick={() => {
                    setTransportistaSel(null);
                    setCabecera((c) => ({ ...c, transportista_id: "" }));
                  }}
                  sx={{ mt: 0.5, textTransform: "none", fontSize: "0.8rem", color: "primary.main" }}
                >
                  Sin transportista
                </Button>
              </Box>

              <TextField
                select
                label="Estado de pago"
                name="estado_pago"
                value={cabecera.estado_pago}
                onChange={handleCabeceraChange}
                fullWidth
                sx={{ flex: "1 1 200px", minWidth: 200 }}
              >
                {ESTADOS_PAGO.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Estado de envío"
                name="estado_envio"
                value={cabecera.estado_envio}
                onChange={handleCabeceraChange}
                fullWidth
                sx={{ flex: "1 1 200px", minWidth: 200 }}
              >
                {ESTADOS_ENVIO.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>

                </Box>
              </AccordionDetails>
            </Accordion>

            {/* === LÍNEAS DE VENTA === */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              Líneas de Venta
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              El precio aplicado se guarda únicamente en esta venta; el precio de lista del producto no se modifica.
            </Typography>
            <TableContainer
              sx={{
                borderRadius: '14px',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                overflowX: 'auto',
                backgroundColor: 'background.paper',
              }}
            >
              <Table size="small" stickyHeader sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Precio de venta</TableCell>
                    <TableCell align="right">Descuento</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.map((ln, idx) => (
                    <TableRow key={idx}>
                      <TableCell width="35%">
                  <AsyncAutocomplete
  label="" 
  value={ln._producto}
  placeholder="Buscar producto..."
  fetchPage={fetchProductosPage}
  getOptionLabel={(opt) => opt?.label || opt?.nombre || ''}   // 👈 CAMBIO
  renderOption={(props, option) => (
    <li {...props} key={option.id} style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column' }}>
      <span style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '4px' }}>
        {option.nombre}
      </span>
      <span style={{ fontSize: '0.8rem', color: '#666', display: 'flex', gap: '6px' }}>
        {option.marca && <span>{option.marca}</span>}
        {option.presentacion && <span>{option.presentacion}</span>}
        <strong>Lista: ${fmtMoney(option.precio_venta)}</strong>
      </span>
    </li>
  )}
  onChange={(val) =>
    setLineas((lines) =>
      lines.map((row, i) =>
        i === idx
          ? {
              ...row,
              _producto: val,
              producto_id: val?.id || '',
              precio_unitario: val?.precio_venta != null ? String(val.precio_venta) : '',
              precio_lista: val?.precio_venta != null ? String(val.precio_venta) : '',
            }
          : row
      )
    )
  }
/>

                      </TableCell>
                      <TableCell align="right" width="10%">
                        <TextField
                          size="small"
                          name="cantidad"
                          type="number"
                          value={ln.cantidad}
                          onChange={(e) => handleLineaChange(idx, e)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="right" width="15%">
                        <TextField
                          size="small"
                          name="precio_unitario"
                          value={ln.precio_unitario}
                          onChange={(e) => handleLineaChange(idx, e)}
                          fullWidth
                          inputProps={{ inputMode: 'decimal' }}
                          placeholder="Precio aplicado"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                        {ln.producto_id && (
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              Lista: ${fmtMoney(precioListaLinea(ln))}
                            </Typography>
                            {tienePrecioEspecial(ln) && (
                              <Chip label="Especial" color="warning" size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                            )}
                            {tienePrecioEspecial(ln) && (
                              <Tooltip title="Restaurar precio de lista">
                                <IconButton size="small" onClick={() => restaurarPrecioLista(idx)} aria-label="Restaurar precio de lista">
                                  <RestartAlt fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </TableCell>

                      <TableCell align="right" width="15%">
                        <TextField
                          size="small"
                          name="descuento"
                          placeholder="0"
                          value={ln.descuento}
                          onChange={(e) => handleLineaChange(idx, e)}
                          fullWidth
                          inputProps={{ inputMode: 'decimal' }}
                          InputProps={{ endAdornment: <span style={{ marginLeft: 4, opacity: 0.6 }}>%</span> }}
                        />
                      </TableCell>
                      <TableCell align="right" width="15%">
                        ${fmtMoney(calcularSubtotal(ln))}
                      </TableCell>
                      <TableCell align="center" width="5%">
                        <IconButton aria-label="Quitar línea" color="error" onClick={() => removeLinea(idx)}>
                          <span style={{ fontSize: 18, fontWeight: 'bold' }}>−</span>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Button startIcon={<AddCircle />} onClick={addLinea} variant="outlined" sx={{ mt: 1 }}>
                        Agregar línea
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Preview de totales */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Resumen</Typography>
                    <Stack spacing={0.5}>
                      <Typography>Subtotal líneas: <strong>${fmtMoney(totalVenta)}</strong></Typography>
                      <Typography>Costo de envío (transportista): <strong>${fmtMoney(costoEnvio)}</strong></Typography>
                      <Typography>Total + Envío: <strong>${fmtMoney(totalConEnvioPreview)}</strong></Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        * La ganancia neta se mostrará al guardar, calculada con el costo real de los lotes y la comisión.
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        * La comisión del transportista se calculará automáticamente al guardar.
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            </fieldset>
          </form>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            backgroundColor: 'action.hover',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Total a cobrar
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              ${fmtMoney(totalConEnvioPreview)}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.25}>
            <Button variant="outlined" onClick={() => nav('/ventas')} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Cancelar
            </Button>
            <Button type="submit" form="venta-form" variant="contained" disabled={locked || loadingVenta || Boolean(loadError) || saving} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {isEdit ? 'Actualizar venta' : 'Guardar venta'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}
